/**
 * useWatchTracker - measures how long each video is actually watched and emits
 * a `video_watched` analytics event when the session ends (slide change,
 * pagehide, or tab hidden), provided it crossed the minimum watch threshold.
 *
 * Watch time is accumulated across pause/resume cycles: `startPlay` stamps a
 * start time, `pausePlay` folds the elapsed delta into `watchedMs`.
 */
import { useRef } from 'preact/hooks';
import { sendEvent, Events } from '../bridge/events.js';

const MIN_WATCH_MS = 1000;

export function useWatchTracker() {
  const session = useRef({
    videoId: null,
    durationMs: 0,
    watchedMs: 0,
    playStartedAt: 0,
    content: '',
  });

  /** Begin a fresh session for a video (resets accumulated time). */
  function beginSession({ videoId, durationMs, content }) {
    session.current = {
      videoId,
      durationMs: durationMs || 0,
      watchedMs: 0,
      playStartedAt: 0,
      content,
    };
  }

  function updateDuration(durationMs) {
    if (durationMs && !Number.isNaN(durationMs)) {
      session.current.durationMs = durationMs;
    }
  }

  function startPlay() {
    const s = session.current;
    if (s.videoId != null && s.playStartedAt === 0) {
      s.playStartedAt = Date.now();
    }
  }

  function pausePlay() {
    const s = session.current;
    if (s.playStartedAt > 0) {
      s.watchedMs += Date.now() - s.playStartedAt;
      s.playStartedAt = 0;
    }
  }

  /** Flush the session: emit the event if it qualifies, then reset the counter. */
  function flush() {
    pausePlay();
    const s = session.current;
    if (s.videoId == null || s.watchedMs < MIN_WATCH_MS) {
      s.watchedMs = 0;
      return;
    }
    sendEvent(Events.VideoWatched, {
      id: s.videoId,
      watched_ms: Math.round(s.watchedMs),
      duration_ms: Math.round(s.durationMs),
      content: s.content,
    });
    s.watchedMs = 0;
  }

  return { session, beginSession, updateDuration, startPlay, pausePlay, flush };
}
